import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import option from 'scoreboard/components/bootslide/option.hbs'
import DatasetFieldSelector from './dataset-field-selector'
import AddDomainObjectEndpoint from './add-domain-object-endpoint'
import { sectionEndpoint } from './object-selector-menu'

/**
 * Builds a two-step bootslide menu for selecting a dataset field. First step is
 * has an org and dataset tree, second step has a field selector
 *
 * @param {BootslideMenu} bootslideMenu
 *
 * @param {String} fieldSelectorTargetId - an ID for the dataset field selector.
 * Must be unique in the entire bootslide menu
 *
 * @param {Object} [fieldSelectorOptions] - options to be passed to the
 * DatasetFieldSelector (2nd step)
 *
 * @param {Function} onFieldSelect - callback that will receive the selected
 * dataset and field. See DatasetFieldList.select() for exact data that's returned
 *
 * @param {String} menuLabel - i18n message key to use for the menu item label
 *
 * @param {String} iconId - ID of the SVG icon to use for the menu item
 *
 * @return an object that can be used as a "target" when building a bootslide menu
 */
function buildDatasetFieldSelectorMenu ({
	bootslideMenu, fieldSelectorTargetId, fieldSelectorOptions, onFieldSelect, menuLabel, iconId }) {

	return {
		label: i18n.get(menuLabel),
		content: _.constant(option({
			icon: iconId,
			label: menuLabel
		})),
		contentEndpoint: sectionEndpoint(bootslideMenu, 'datasets', buildDatasetOption({
			bootslideMenu,
			fieldSelectorTargetId
		})),
		target: [ buildFieldSelectorTarget({
			bootslideMenu,
			targetId: fieldSelectorTargetId,
			fieldSelectorOptions,
			onFieldSelect
		})]
	}
}

/**
 * Builds the menu option passed to sectionEndpoint(), which renders the
 * org/dataset selector (1st step)
 */
function buildDatasetOption ({ bootslideMenu, fieldSelectorTargetId }) {
	return AddDomainObjectEndpoint.extend({
		buttonLabelCode: 'common.chooseNoEllipsis',

		add: function (dataset, org) {
			bootslideMenu.bootslide.navigate(fieldSelectorTargetId, {
				datasetId: dataset.id,
				organizationId: org.id
			})
		},

		selectorOptions: function (opt) {
			let selectorOptions = AddDomainObjectEndpoint.prototype.selectorOptions.apply(this, opt)
			selectorOptions.objectParams = {
				// Can't pick a dataset field for dataset views
				topLevelOnly: true
			}

			return selectorOptions
		},

		allowSelect: function (node) {
			if (!AddDomainObjectEndpoint.prototype.allowSelect.apply(this, arguments)) {
				return false
			}

			// Can only select a dataset field for "real" datasets, not folders
			// or views
			return node.type === 'dataset'
		}
	})
}

/**
 * Builds the target for the dataset field selector (2nd step)
 */
function buildFieldSelectorTarget ({ bootslideMenu, targetId, fieldSelectorOptions, onFieldSelect }) {
	let fieldSelector = new DatasetFieldSelector(fieldSelectorOptions)

	// Adjusts the widths of the trees when necessary (after transition or
	// loading a new tree)
	function adjustMenu () {
		bootslideMenu.setTreeWidths({
			el: fieldSelector.$el,
			gardeners: _.get(fieldSelector, 'fieldList.gardener')
				? [ fieldSelector.fieldList.gardener ] : [],
			types: ['gardener']
		})
	}

	return {
		label: i18n.get('datasets.dataset.field'),
		id: targetId,
		contentEndpoint: function () {
			// After sliding transition is complete, need to adjust the menu
			// to fit the tree (though it may still be loading)
			bootslideMenu.$('.bootslide-container').one('transitionend', adjustMenu)

			bootslideMenu.childViews.push(fieldSelector)

			fieldSelector
				.on('select', onFieldSelect)
				.on('fetch', () => {
					// Reset menu to the default width until the new list of
					// fields is loaded (the 'ready' event). 600px comes from
					// .dataset-field-selector styling in widget-config.scss
					bootslideMenu.setWidth({ menuOptionEl: fieldSelector.$el, menuWidth: 600 })
				})
				// Once the tree is loaded, adjust the menu to fit the tree,
				// because it may have been loading when the sliding transition
				// completed
				.on('ready', (opt = {}) => {
					if (opt.fromXHR) {
						// Only adjust menu if it was rendered after an XHR;
						// otherwise, it will automatically transition to the
						// correct width
						adjustMenu()
					}
				})

			return fieldSelector.render().el
		},
		onNavigate: function ({ datasetId, organizationId }) {
			fieldSelector.setDatasetId(datasetId)
		}
	}
}

export default buildDatasetFieldSelectorMenu

buildDatasetFieldSelectorMenu.__filename__ = 'components/bootslide/build-dataset-field-selector-menu.js'
